import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Link from '@mui/icons-material/Link';
import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { t } from 'i18next';
import React from 'react';

interface Props<TOption extends Record<string, unknown> = Record<string, unknown>> {
  getLabel: (value: TOption) => string;
  inputValue: string;
  isCheckEnabled?: boolean;
  onDelete?: () => void;
  option: TOption;
  props: React.HTMLAttributes<HTMLElement>;
  // Optional secondary line rendered beneath the name. Callers opt in by
  // composing this from their own option shape — the renderer itself reads no
  // fields off `option`, so adding a typeahead doesn't risk an accidental
  // second row.
  secondary?: React.ReactNode;
  selected?: boolean;
  showLinkIcon?: boolean;
  wrapLabel?: boolean;
}
const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const SelectOption = <TOption extends Record<string, unknown> = Record<string, unknown>>({
  getLabel,
  inputValue,
  isCheckEnabled = true,
  onDelete,
  option,
  props,
  secondary,
  selected = false,
  showLinkIcon = false,
  wrapLabel = false,
}: Props<TOption>) => {
  const str = getLabel(option);
  const matches = match(str, inputValue, {
    insideWords: true,
  });
  const parts = parse(str, matches);
  const theme = useTheme();
  const hasSecondary = Boolean(secondary);

  return (
    <Box
      component="li"
      {...props}
      sx={{
        // Fixed min-height so single-line (name only) and stacked (name +
        // secondary) rows align to the same row rhythm across all typeaheads.
        // !important needed to beat MUI's own min-height on `.MuiAutocomplete-option`.
        minHeight: '48px !important',
        // !important needed to override MUI's `.MuiAutocomplete-option` default
        // `display: flex; align-items: center` which keeps children on one row.
        // Column + center keeps the name visually centered when there is no
        // secondary, while stacking name above secondary when present.
        ...(hasSecondary && {
          display: 'flex !important',
          flexDirection: 'column !important',
          justifyContent: 'center !important',
          alignItems: 'stretch !important',
        }),
        borderLeft: '3px solid transparent',
        // !important needed to override MUI's default `.MuiAutocomplete-option.Mui-focused`
        // background which sits at the same specificity as our sx-generated class.
        '&.Mui-focused, &[aria-selected="true"], &:hover': {
          backgroundColor: `${alpha(theme.brandV2.colors.treadYellow, 0.15)} !important`,
          borderLeftColor: theme.brandV2.colors.treadYellow,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        {isCheckEnabled && (
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: '8px', padding: 0 }}
            checked={selected}
          />
        )}
        {showLinkIcon && (
          <Link
            sx={{ color: theme.brandV2.colors.treadGreen, fontSize: '20px', mr: 1 }}
          />
        )}
        <Tooltip
          title={str}
          placement="right-start"
          enterTouchDelay={500}
          enterDelay={500}
          enterNextDelay={500}
        >
          <Typography
            noWrap={!wrapLabel}
            variant="body2"
            sx={{
              flex: '1',
              fontWeight: 500,
              ...(wrapLabel && {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }),
            }}
          >
            {parts.map((part, index) => (
              <span
                key={`${part?.text}-${index}`}
                style={{ fontWeight: part.highlight ? 700 : 500 }}
              >
                {/* Spaces won't be rendered in a span so we replace with nbsp character */}
                {wrapLabel ? part.text : String(part.text ?? '').replaceAll(' ', ' ')}
              </span>
            ))}
          </Typography>
        </Tooltip>
        {onDelete && (
          <Box>
            <Tooltip
              title={t('actions.delete')}
              placement="right-start"
              enterTouchDelay={500}
              enterDelay={500}
              enterNextDelay={500}
            >
              <Typography noWrap variant="body2">
                <DeleteOutline
                  fontSize="small"
                  style={{ padding: 0, color: `${theme.brandV2.colors.treadBlack}` }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete?.();
                  }}
                />
              </Typography>
            </Tooltip>
          </Box>
        )}
      </Box>
      {hasSecondary && (
        <Box sx={{ display: 'flex', gap: 2, mt: 0.25, alignItems: 'baseline' }}>
          {secondary}
        </Box>
      )}
    </Box>
  );
};

export { SelectOption };
